/**
 * @class VideoPlayer
 * @extends HTMLElement
 *
 * A custom web component for rendering video content using the Plyr library.
 * Supports HTML5 video as well as embedded YouTube and Vimeo sources.
 *
 * Plyr.js is used to provide a consistent, accessible player UI across all sources.
 */

export class VideoPlayer extends HTMLElement {
  #plyr = null;

  get host() {
    return this.getAttribute("host") || "html";
  }

  get plyrOptions() {
    return {
      playsinline: true,
      hideControls: false,
      controls: [
        "play-large",
        "play",
        "progress",
        "current-time",
        "mute"
      ]
    };
  }

  connectedCallback() {
    this.#initPlayer();
  }

  disconnectedCallback() {
    this.#destroyPlayer();
  }

  #initPlayer() {
    const targetElement = this.host === "html"
      ? this.querySelector("video")
      : this.querySelector("[data-plyr-embed]");

    if (!targetElement) return;

    this.#plyr = new Plyr(targetElement, this.plyrOptions);
  }

  #destroyPlayer() {
    this.#plyr?.destroy();
    this.#plyr = null;
  }

  play() {
    this.#plyr?.play();
  }

  pause() {
    this.#plyr?.pause();
  }

  get paused() {
    return this.#plyr?.paused ?? true;
  }
}

if (!customElements.get("video-player")) {
  customElements.define("video-player", VideoPlayer);
}
